<script setup lang="ts">
import { watch } from 'vue'
import { useForm } from '@inertiajs/vue3'
import Modal from '@/Components/Utils/Modal.vue'
import Button from '@/Components/Utils/Button.vue'

const props = defineProps<{
    isOpen: boolean
    brandId: number
    products: { id: number, display_name: string }[]
    orders: { id: number, reference: string, state_label?: string }[]
}>()

const emits = defineEmits<{
    (e: 'onClose'): void
}>()

const form = useForm({
    brand_product_id: null as number | null,
    cs_order_id: null as number | null,
    name: '',
    link: '',
    note: '',
})

watch(() => props.isOpen, (isOpen) => {
    if (isOpen) {
        form.reset()
        form.clearErrors()
    }
})

const submit = () => {
    form.post(route('kasir.brands.designs.store', props.brandId), {
        preserveScroll: true,
        onSuccess     : () => emits('onClose'),
    })
}
</script>

<template>
    <Modal :isOpen="isOpen" width="w-full max-w-lg" closeButton @onClose="emits('onClose')">
        <div class="flex flex-col gap-4">
            <div>
                <div class="pr-8 text-base font-semibold text-gray-800">Tambah desain</div>
                <div class="text-xs text-gray-500">Desain otomatis masuk ke brand ini.</div>
            </div>

            <div>
                <label class="mb-1 block text-sm text-gray-600">Nama desain <span class="text-red-500">*</span></label>
                <input v-model="form.name" placeholder="Desain Lebaran 2026" class="w-full rounded-md border-gray-300 text-sm" />
                <div v-if="form.errors.name" class="mt-1 text-xs text-red-500">{{ form.errors.name }}</div>
            </div>

            <div>
                <label class="mb-1 block text-sm text-gray-600">Link desain <span class="text-red-500">*</span></label>
                <input v-model="form.link" placeholder="https://drive.google.com/..." class="w-full rounded-md border-gray-300 text-sm" />
                <div class="mt-1 text-xs text-gray-400">Link Google Drive (folder atau file).</div>
                <div v-if="form.errors.link" class="mt-1 text-xs text-red-500">{{ form.errors.link }}</div>
            </div>

            <div>
                <label class="mb-1 block text-sm text-gray-600">Produk brand</label>
                <select v-model="form.brand_product_id" class="w-full rounded-md border-gray-300 text-sm">
                    <option :value="null">Semua produk brand ini</option>
                    <option v-for="product in products" :key="product.id" :value="product.id">
                        {{ product.display_name }}
                    </option>
                </select>
                <div v-if="form.errors.brand_product_id" class="mt-1 text-xs text-red-500">{{ form.errors.brand_product_id }}</div>
            </div>

            <div>
                <label class="mb-1 block text-sm text-gray-600">Lampirkan ke pesanan</label>
                <select v-model="form.cs_order_id" class="w-full rounded-md border-gray-300 text-sm">
                    <option :value="null">Tidak dilampirkan</option>
                    <option v-for="order in orders" :key="order.id" :value="order.id">
                        {{ order.reference }}<template v-if="order.state_label"> · {{ order.state_label }}</template>
                    </option>
                </select>
                <div class="mt-1 text-xs text-gray-400">Opsional, dari 10 pesanan terakhir brand ini. Bisa juga diatur dari halaman pesanan.</div>
                <div v-if="form.errors.cs_order_id" class="mt-1 text-xs text-red-500">{{ form.errors.cs_order_id }}</div>
            </div>

            <div>
                <label class="mb-1 block text-sm text-gray-600">Catatan</label>
                <textarea v-model="form.note" rows="3" placeholder="Contoh: warna tutup pakai Pantone 185C" class="placeholder:opacity-70 w-full rounded-md border-gray-300 text-sm" />
                <div class="mt-1 text-xs text-gray-400">
                    Catatan akan ditampilkan di halaman Manufacture dan dibaca oleh staff produksi.
                </div>
                <div v-if="form.errors.note" class="mt-1 text-xs text-red-500">{{ form.errors.note }}</div>
            </div>

            <div class="flex flex-col-reverse gap-2 border-t border-gray-100 pt-3 sm:flex-row sm:justify-end">
                <Button full :style="'tertiary'" label="Batal" @click="emits('onClose')" />
                <Button
                    full
                    :style="'save'"
                    label="Simpan"
                    :loading="form.processing"
                    @click="submit"
                />
            </div>
        </div>
    </Modal>
</template>
